import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import AIRequestNotifications from '../AIRequestNotifications';
import { useWorkspaceLayout } from '../../context/WorkspaceLayoutContext';
import { formatShortcut } from '../../hooks/useWorkspaceShortcuts';
import { InkyMascot } from '../brand';

/**
 * Menu button for editor page headers. Editor pages hide the shell's sidebar (see
 * `useEditorChrome`); this opens it as a drawer so every page stays one click away. Hidden in
 * focus mode, where the shell renders no navigation at all.
 * @returns {JSX.Element|null} Button.
 */
export function EditorNavButton() {
  const { setMobileNavOpen, mobileNavOpen, focusMode } = useWorkspaceLayout();
  if (focusMode) return null;
  return (
    <Tooltip title="Open navigation">
      <IconButton
        aria-label="Open navigation"
        aria-haspopup="true"
        aria-expanded={mobileNavOpen ? 'true' : 'false'}
        onClick={() => setMobileNavOpen(true)}
        sx={{ color: 'var(--q-muted, #5E5A54)' }}
      >
        <MenuIcon />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Reopens Inky from an editor header. Rendered only while the Inky panel is closed, so the
 * panel stays the single Inky surface when it is open (the shell's top bar toggle is hidden on
 * editor pages). Mod+J keeps working either way.
 * @returns {JSX.Element|null} Button or null while the panel is open.
 */
export function InkyReopenButton() {
  const { dock, openInky } = useWorkspaceLayout();
  if (dock.open) return null;
  const label = `Open Inky (${formatShortcut('Mod+J')})`;
  return (
    <Tooltip title={label}>
      <IconButton
        aria-label="Open Inky"
        aria-controls="inky-dock"
        aria-expanded="false"
        onClick={() => openInky({ focus: true })}
        sx={{ bgcolor: 'var(--q-ai-tint)', '&:hover': { bgcolor: 'var(--q-ai-tint-strong)' } }}
      >
        <InkyMascot size={24} decorative animated={false} />
      </IconButton>
    </Tooltip>
  );
}

/**
 * AI job progress for editor headers. The shell's top bar (which normally holds it) is hidden on
 * editor pages, so the editors render the same control: running jobs, cancel, and links to
 * finished results. It hides itself while no jobs exist.
 * @returns {JSX.Element} Jobs control.
 */
export function EditorJobsButton() {
  return <AIRequestNotifications />;
}
